import { Dialog, DialogContent, Avatar, Typography, Box } from "@mui/material";
import ArgonButton from "components/ArgonButton";
import { useEffect, useState, useRef, forwardRef } from "react";
import { toast } from "react-toastify";
import { getReportById, resolveReport } from "services/reportService";
import CircularProgress from "@mui/material/CircularProgress";
import { REPORT_ACTIONS } from "./consts";
import "./ReviewPanelModal.css";
import AvatarWithLoader from "../Avatar/AvatarWithLoader";
import { formatReasonOrContentType } from "services/utilities";

function ChatReviewPanelModal({ open, handleClose, _id, onUpdate }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const flaggedMsgRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    try {
      getReportById(_id).then((data) => {
        setReport(data?.data);
        setLoading(false);
      });
    } catch (error) {
      console.error(error);
    }
  }, [_id]);

  useEffect(() => {
    if (!loading && open && report) {
      setTimeout(() => {
        if (flaggedMsgRef.current) {
          flaggedMsgRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    }
  }, [loading, open, report]);

  const handleAction = (action) => {
    resolveReport(_id, action).then((res) => {
      if (res.success) {
        toast.info("Action taken successfully");
        handleClose();
        onUpdate();
      } else {
        toast.error("Failed to take action");
      }
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box display="flex" justifyContent="center" alignItems="center" pt={3}>
        <Typography fontSize={20} fontWeight={600} color={"#344767"}>
          Content Review Panel
        </Typography>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" pt={3}>
          <CircularProgress />
        </Box>
      ) : (
        <DialogContent sx={{ p: 4, backgroundColor: "#fefefe" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
            mb={3}
            sx={{ flexWrap: "wrap", gap: 2 }}
          >
            <MetaItem
              label="Reported User"
              name={report?.reportedAgainst?.name}
              img={report?.reportedAgainst?.avatar}
            />
            <MetaItem
              label="Reported By"
              name={report?.reporter?.name}
              img={report?.reporter?.avatar}
            />
            <MetaItem
              label="Content Type"
              value={formatReasonOrContentType(report?.contentType)}
            />
            <MetaItem
              label="Report Reason"
              value={formatReasonOrContentType(report?.reason)}
            />
          </Box>

          {/* Chat Group Title */}
          <Box
            sx={{
              backgroundColor: "#fff",
              p: 1,
              borderRadius: "10px 10px 0 0",
              boxShadow: 3,
              mb: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Avatar
              src={report?.content?.activity?.image || "/avatars/default.jpg"}
              variant="rounded"
              sx={{ width: 50, height: 50 }}
            />
            <Box>
              <Typography fontSize={16} fontWeight={600} color={"#172B4D"}>
                {report?.content?.activity?.title || "Chat Activity"}
              </Typography>
              <Typography fontSize={12} fontWeight={400} color={"#8392AB"}>
                {report?.content?.activity?.membersCount || 0} Members
              </Typography>
            </Box>
          </Box>

          {/* Chat Timeline */}
          <Box
            ref={chatContainerRef}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "0 0 10px 10px",
              boxShadow: 3,
              p: 2,
              maxHeight: 400,
              overflowY: "auto",
            }}
          >
            {report?.content?.messages?.map((msg, i) => (
              <ChatBubble
                key={msg._id || i}
                message={msg}
                id={`${msg.isFlagged ? "flagged" : ""}`}
                loading={loading}
                ref={msg.isFlagged ? flaggedMsgRef : null}
              />
            )) || (
              <Typography color="#8392AB" textAlign="center">
                No messages found
              </Typography>
            )}
          </Box>

          <Box display="flex" justifyContent="center" mt={4} gap={1}>
            {report?.isResolved ? (
              <ArgonButton
                className="review-panel-action-button"
                onClick={handleClose}
              >
                Close
              </ArgonButton>
            ) : (
              <>
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={handleClose}
                >
                  Cancel
                </ArgonButton>
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={() => handleAction(REPORT_ACTIONS.WARN)}
                >
                  ⚠️ Warn
                </ArgonButton>
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={() => handleAction(REPORT_ACTIONS.MUTE_USER)}
                >
                  🤐 Mute User
                </ArgonButton>
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={() => handleAction(REPORT_ACTIONS.DELETE_CONTENT)}
                >
                  ❌ Delete Message
                </ArgonButton>
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={() => handleAction(REPORT_ACTIONS.BAN_USER)}
                >
                  ⛔ Ban User
                </ArgonButton>
                <ArgonButton
                  className="review-panel-action-button"
                  onClick={() => handleAction(REPORT_ACTIONS.DISMISS)}
                >
                  ✅ Dismiss Complain
                </ArgonButton>
              </>
            )}
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}

function MetaItem({ label, name, value, img }) {
  return (
    <Box display="flex" flexDirection="column" alignItems="left" gap={1}>
      <Typography variant="h6" fontWeight={700} color="#000000">
        {label}
      </Typography>
      <Box display="flex" flexDirection="row" alignItems="center">
        {img && <AvatarWithLoader src={img} alt={name} />}
        <Box pb={1}>
          <Typography variant="h6" fontWeight={700} color="#7B809A">
            {name || value}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const ChatBubble = forwardRef(({ message }, ref) => {
  const isMyMessage = message.sender?.isMe === true;
  const bubbleColor = message.isFlagged
    ? "#f87171"
    : isMyMessage
    ? "#dee2e6"
    : "#f8f8f8";

  return (
    <Box
      ref={ref}
      display="flex"
      justifyContent={isMyMessage ? "flex-end" : "flex-start"}
      mb={1}
    >
      {!isMyMessage && (
        <AvatarWithLoader
          src={message.sender?.avatar}
          alt={message.sender?.name}
        />
      )}
      <Box
        sx={{
          backgroundColor: bubbleColor,
          borderRadius: "10px",
          boxShadow: 1,
          p: 1,
          maxWidth: "75%",
        }}
      >
        {!isMyMessage && (
          <Typography
            fontSize={12}
            fontWeight={600}
            color={message.isFlagged ? "#fff" : "#172B4D"}
            mb={0.5}
          >
            {message.sender?.name || "Unknown User"}
          </Typography>
        )}
        <Typography
          fontSize={14}
          fontWeight={400}
          color={message.isFlagged ? "#fff" : "#8392AB"}
        >
          {message.message}
        </Typography>
        <Typography
          variant="caption"
          display="block"
          textAlign="right"
          color={message.isFlagged ? "#fff" : "#8392AB"}
        >
          {message.time} {message.date && `• ${message.date}`}
        </Typography>
      </Box>
      {isMyMessage && (
        <AvatarWithLoader
          src={message.sender?.avatar}
          alt={message.sender?.name}
          leftMargin={true}
        />
      )}
    </Box>
  );
});

export default ChatReviewPanelModal;
